import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/lib/supabaseClient";

function readAdminCache(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAdminCache(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(Array.isArray(value) ? value : []));
  } catch {
    // ignore
  }
}

function toAdminErrorMessage(error, fallback) {
  const message = error?.message || "";
  const normalized = message.toLowerCase();

  if (
    normalized.includes("failed to fetch") ||
    normalized.includes("timed out") ||
    normalized.includes("network") ||
    normalized.includes("request object could not be cloned") ||
    normalized.includes("datacloneerror")
  ) {
    return "Cannot reach Supabase right now. Check VPN/proxy/firewall and try again.";
  }

  return message || fallback;
}

function TestimonialCard({ t, onApprove, onHold, onDelete, busy }) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <p className="font-semibold text-[#0A0A0A]">{t.name}</p>
            <span className="text-xs px-2 py-0.5 rounded-full bg-[#097DC3]/10 text-[#097DC3] font-medium">
              {t.category}
            </span>
            <span
              className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                t.status === "approved"
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-amber-50 text-amber-700"
              }`}
            >
              {t.status}
            </span>
          </div>
          <p className="text-sm text-[#475569] mt-1">{t.role}</p>
          <p className="text-xs text-[#097DC3] font-medium mt-1">{t.program}</p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {t.status === "pending" && (
            <button
              type="button"
              onClick={() => onApprove(t)}
              disabled={busy}
              className="px-3 py-2 rounded-xl bg-[#1CC487] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              Approve
            </button>
          )}
          {t.status === "approved" && (
            <button
              type="button"
              onClick={() => onHold(t)}
              disabled={busy}
              className="px-3 py-2 rounded-xl bg-amber-100 border border-amber-200 text-amber-700 text-sm font-semibold hover:bg-amber-100 disabled:opacity-50"
            >
              Hold
            </button>
          )}
          <button
            type="button"
            onClick={() => onDelete(t)}
            disabled={busy}
            className="px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-[#0A0A0A] hover:bg-slate-50 disabled:opacity-50"
          >
            Delete
          </button>
        </div>
      </div>

      <p className="text-sm text-[#0A0A0A] leading-relaxed mt-4">{t.outcome}</p>
    </div>
  );
}

export default function AdminPanel() {
  const [session, setSession] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const cacheKey = useMemo(
    () => `uproot:admin:testimonials:${statusFilter}:${categoryFilter}`,
    [statusFilter, categoryFilter]
  );
  const [items, setItems] = useState(() => readAdminCache(cacheKey));

  const pendingCount = useMemo(
    () => items.filter((t) => t.status === "pending").length,
    [items]
  );

  useEffect(() => {
    setItems(readAdminCache(cacheKey));
  }, [cacheKey]);

  async function load() {
    if (!session) return;
    setError("");
    try {
      let query = supabase
        .from("testimonials")
        .select("id,name,role,category,program,outcome,status,created_at,approved_at")
        .order("created_at", { ascending: false })
        .limit(500);

      if (statusFilter !== "all") query = query.eq("status", statusFilter);
      if (categoryFilter !== "all") query = query.eq("category", categoryFilter);

      const { data, error: qErr } = await query;
      if (qErr) throw qErr;
      const safe = Array.isArray(data) ? data : [];
      setItems(safe);
      writeAdminCache(cacheKey, safe);
    } catch (e) {
      setError(toAdminErrorMessage(e, "Failed to load testimonials"));
    }
  }

  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  });

  useEffect(() => {
    let unsub;

    (async () => {
      try {
        const { data, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) throw sessionError;
        setSession(data?.session ?? null);
      } catch {
        setSession(null);
      }
    })();

    const { data: listener } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });
    unsub = listener?.subscription;

    return () => {
      unsub?.unsubscribe?.();
    };
  }, []);

  useEffect(() => {
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, statusFilter, categoryFilter]);

  // Auto-refresh list when the underlying table changes.
  useEffect(() => {
    if (!session) return undefined;

    let t;
    const debounceMs = 600;
    const channel = supabase.channel("rt:admin:testimonials");

    const onChange = () => {
      clearTimeout(t);
      t = setTimeout(() => {
        loadRef.current?.();
      }, debounceMs);
    };

    channel.on(
      "postgres_changes",
      { event: "*", schema: "public", table: "testimonials" },
      onChange
    );
    channel.subscribe();

    return () => {
      clearTimeout(t);
      supabase.removeChannel(channel);
    };
  }, [session]);

  async function handleLogin(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (signInError) throw signInError;
      setPassword("");
    } catch (e) {
      setError(toAdminErrorMessage(e, "Login failed"));
    } finally {
      setBusy(false);
    }
  }

  async function handleLogout() {
    await supabase.auth.signOut();
    setItems([]);
  }

  async function approveTestimonial(t) {
    setBusy(true);
    setError("");
    try {
      const { error: upErr } = await supabase
        .from("testimonials")
        .update({ status: "approved", approved_at: new Date().toISOString() })
        .eq("id", t.id);
      if (upErr) throw upErr;
      await load();
    } catch (e) {
      setError(toAdminErrorMessage(e, "Approve failed"));
    } finally {
      setBusy(false);
    }
  }

  async function holdTestimonial(t) {
    setBusy(true);
    setError("");
    try {
      const { error: upErr } = await supabase
        .from("testimonials")
        .update({ status: "pending", approved_at: null })
        .eq("id", t.id);
      if (upErr) throw upErr;
      await load();
    } catch (e) {
      setError(toAdminErrorMessage(e, "Hold failed"));
    } finally {
      setBusy(false);
    }
  }

  async function deleteTestimonial(t) {
    setBusy(true);
    setError("");
    try {
      const { error: delErr } = await supabase.from("testimonials").delete().eq("id", t.id);
      if (delErr) throw delErr;
      await load();
    } catch (e) {
      setError(toAdminErrorMessage(e, "Delete failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="bg-gradient-to-br from-[#8CDDF6]/20 via-[#8CDDF6]/10" data-testid="admin-panel">
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 relative">
          <div className="flex items-start justify-between gap-6">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-[#097DC3]">Admin</p>
              <h1 className="font-['Outfit'] text-3xl md:text-4xl font-semibold tracking-tight text-[#0A0A0A] mt-2">
                Admin Panel
              </h1>
            </div>

            {session && (
              <button
                type="button"
                onClick={handleLogout}
                className="px-4 py-2 rounded-xl border border-slate-200 bg-red-400 text-sm font-semibold text-[#0A0A0A] hover:bg-slate-50"
              >
                Logout
              </button>
            )}
          </div>

          {error && (
            <div className="mt-6 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 px-4 py-3 text-sm">
              {error}
            </div>
          )}

          {!session ? (
            <div className="mt-10 max-w-md">
              <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <h2 className="font-['Outfit'] text-xl font-semibold text-[#0A0A0A]">Sign in</h2>
                <p className="text-sm text-[#475569] mt-1">Use your Supabase admin account.</p>

                <form onSubmit={handleLogin} className="mt-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0A] mb-2">Email</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:ring-2 focus:ring-[#097DC3]/30"
                      placeholder="admin@yourdomain.com"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0A] mb-2">Password</label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:ring-2 focus:ring-[#097DC3]/30"
                      placeholder="••••••••"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={busy}
                    className="w-full px-4 py-3 rounded-xl bg-[#097DC3] text-white font-semibold hover:opacity-90 disabled:opacity-50"
                  >
                    {busy ? "Signing in…" : "Sign in"}
                  </button>
                </form>
              </div>
            </div>
          ) : (
            <div className="mt-10">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-[#475569]">
                    Pending: <span className="font-semibold text-[#0A0A0A]">{pendingCount}</span>
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#475569]">Category</span>
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  >
                    <option value="all">All</option>
                    <option value="client">Client</option>
                    <option value="student">Student</option>
                  </select>

                  <span className="text-sm text-[#475569]">Show</span>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  >
                    <option value="all">All</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                  </select>

                  <button
                    type="button"
                    onClick={load}
                    disabled={busy}
                    className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-[#0A0A0A] hover:bg-slate-50 disabled:opacity-50"
                  >
                    Refresh
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4">
                {items.length === 0 ? (
                  <div className="rounded-2xl border border-slate-100 bg-white p-8 text-[#475569]">
                    No testimonials found.
                  </div>
                ) : (
                  items.map((t) => (
                    <TestimonialCard
                      key={t.id}
                      t={t}
                      busy={busy}
                      onApprove={approveTestimonial}
                      onHold={holdTestimonial}
                      onDelete={deleteTestimonial}
                    />
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
